/**
 * @copyright 2026 Cooltrax Pty Ltd. All rights reserved.
 */

import React from 'react'

export type BulkMode = 'replace' | 'add' | 'remove'

/** How the checked set applies to every selected item, described for a given plural noun. */
export const bulkModes = (subject: string): { value: BulkMode; label: string; description: string }[] => [
  {
    value: 'replace',
    label: 'Replace',
    description: `Set each selected ${subject.replace(/s$/, '')}’s apps and insights to exactly what is checked`,
  },
  {
    value: 'add',
    label: 'Add',
    description: `Turn on the checked apps and insights for the selected ${subject}, leaving everything else as it is`,
  },
  {
    value: 'remove',
    label: 'Remove',
    description: `Turn off the checked apps and insights for the selected ${subject}`,
  },
]

export const BULK_MODES = bulkModes('users')

interface Props {
  value: BulkMode
  onChange: (mode: BulkMode) => void
  disabled?: boolean
  /** Plural noun for the legend and descriptions — "users" (default) or "companies". */
  subject?: string
}

/** Replace / Add / Remove — how the checked set applies to every selected user or company. */
export const ModeSelector: React.FC<Props> = ({ value, onChange, disabled, subject = 'users' }) => (
  <fieldset className="mt-2 flex flex-col gap-2" data-testid="mode-selector">
    <legend className="label-text mb-1 font-medium">What should happen to the selected {subject}?</legend>
    {bulkModes(subject).map((mode) => (
      <div key={mode.value} className="flex items-start gap-3">
        <input
          id={`mode-${mode.value}`}
          type="radio"
          name="bulk-mode"
          className="radio radio-primary radio-sm mt-0.5"
          value={mode.value}
          checked={value === mode.value}
          disabled={disabled}
          onChange={() => onChange(mode.value)}
          data-testid={`mode-${mode.value}`}
        />
        <label htmlFor={`mode-${mode.value}`} className="cursor-pointer">
          <span className="label-text font-medium">{mode.label}</span>
          <span className="block text-xs text-base-content/60">{mode.description}</span>
        </label>
      </div>
    ))}
  </fieldset>
)

export default ModeSelector
